/**
 * AI policy upload page (M19.7) at /policies/ai-ingestion (moved
 * from /ai/ingestion/upload in M26 PR 2 — its output is policy
 * config so it lives under the Policies module).
 *
 * Drag-drop PDF, select legal entity (?le=<uuid> for now), submit →
 * synchronously runs the M19.5 pipeline. On success navigates to the
 * draft review page.
 */

import { useMutation, useQuery } from "@tanstack/react-query";
import { FileUp, Sparkles } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { PageHeader } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { useLegalEntityGate } from "../lib/legalEntity";

export function PolicyUploadPage() {
  const { t } = useTranslation();
  const { legalEntityId: le, gate } = useLegalEntityGate();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const recentDrafts = useQuery({
    queryKey: ["policy-drafts", le],
    queryFn: () => api.listPolicyDrafts(le),
    enabled: !!le,
  });

  const upload = useMutation({
    mutationFn: () => api.uploadPolicyPdf(le, file!, "hr"),
    onSuccess: (draft) =>
      navigate(`/policies/ai-ingestion/drafts/${draft.id}`),
    onError: (err) => {
      setErrorMsg(err instanceof ApiError ? err.detail : String(err));
    },
  });

  if (gate) return gate;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("nav.policy_ingestion")}
        subtitle={t("policy.upload_subtitle")}
        breadcrumbs={[
          { label: t("nav.policies"), href: "/policies" },
          { label: t("nav.ai_ingestion") },
        ]}
      />

      <article className="rounded-xl border border-border-subtle bg-surface-default p-5">
        <header className="flex items-center gap-2">
          <Sparkles size={18} className="text-brand" />
          <h2 className="text-base font-semibold">
            {t("policy.upload_heading")}
          </h2>
        </header>
        <p className="mt-1 text-sm text-text-secondary">
          {t("policy.upload_description")}
        </p>
        <label
          htmlFor="policy-file"
          className="mt-4 flex h-32 cursor-pointer flex-col items-center justify-center gap-1 rounded border-2 border-dashed border-border-default bg-surface-muted text-sm text-text-secondary hover:border-brand"
        >
          <FileUp size={24} />
          <span>{file ? file.name : t("policy.upload_dropzone")}</span>
          <input
            id="policy-file"
            type="file"
            accept="application/pdf"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setFile(f);
              setErrorMsg(null);
            }}
          />
        </label>
        {errorMsg && (
          <p className="mt-3 rounded bg-rose-50 p-2 text-xs text-rose-700">
            {errorMsg}
          </p>
        )}
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => {
              if (file) {
                setErrorMsg(null);
                upload.mutate();
              }
            }}
            disabled={!file || upload.isPending}
            className="rounded bg-brand px-4 py-2 text-sm font-medium text-brand-fg hover:bg-brand-hover disabled:opacity-50"
          >
            {upload.isPending
              ? t("policy.extracting")
              : t("policy.upload_cta")}
          </button>
        </div>
      </article>

      <section>
        <h2 className="mb-2 text-sm font-semibold text-text-primary">
          {t("policy.recent_drafts")}
        </h2>
        {recentDrafts.isLoading ? (
          <Loading />
        ) : recentDrafts.error ? (
          <ErrorPanel
            message={(recentDrafts.error as Error).message}
          />
        ) : (recentDrafts.data ?? []).length === 0 ? (
          <p className="text-xs text-text-secondary">
            {t("policy.no_drafts")}
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {(recentDrafts.data ?? []).map((d) => (
              <li
                key={d.id}
                className="flex items-center justify-between rounded border border-border-subtle bg-surface-default p-2 text-xs"
              >
                <button
                  type="button"
                  onClick={() =>
                    navigate(`/policies/ai-ingestion/drafts/${d.id}`)
                  }
                  className="font-mono text-brand hover:underline"
                >
                  {d.id.slice(0, 8)}
                </button>
                <span className="rounded bg-surface-muted px-1.5 py-0.5">
                  {d.status}
                </span>
                <span className="text-text-secondary">
                  {d.extracted_domains.length}/8 domains
                </span>
                <span className="text-text-secondary">
                  {d.page_count} pages
                </span>
                <span className="text-text-secondary">
                  {d.created_at.slice(0, 10)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
